/**
 * @module components/Controls/ControlsTable
 * @description Tabla principal de controles ISO 27002 con edición inline y gestión de asignaciones.
 *
 * Renderiza la lista paginada de controles con estado de cumplimiento, porcentaje de progreso,
 * referentes asignados y fechas límite. Soporta:
 * - Ordenamiento por columna (código, estado, progreso, fecha)
 * - Edición inline de estado y fecha directamente en la fila
 * - Apertura del `ControlAssignmentModal` para asignar referentes
 * - Selección de control para abrir el `DetailPanel`
 *
 * ## Permisos
 * - `admin`: puede editar estado, fecha y asignar referentes
 * - `technical_referent`: puede editar estado de controles asignados a él
 * - `auditor` / `viewer`: solo puede ver; los botones de edición no se renderizan
 *
 * @see DetailPanel que se abre al seleccionar un control
 * @see ControlAssignmentModal para la asignación de referentes técnicos
 * @see DataContext para las funciones updateControl y assignControlToReferent
 */
import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Eye,
  Edit,
  Users,
  Calendar,
  AlertCircle,
  CheckCircle,
  Clock,
  UserPlus,
  User,
  Save,
  X
} from 'lucide-react';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { ISOControl, SelectedControl } from '../../types/iso';
import { ControlAssignmentModal } from './ControlAssignmentModal';

interface ControlsTableProps {
  onSelectControl: (control: SelectedControl | null) => void;
}

export function ControlsTable({ onSelectControl }: ControlsTableProps) {
  const { filteredControls, assignControlToReferent, updateControl } = useData();
  const { user } = useAuth();
  const [sortConfig, setSortConfig] = useState<{
    key: keyof ISOControl;
    direction: 'asc' | 'desc';
  } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [assignmentModal, setAssignmentModal] = useState<ISOControl | null>(null);
  const [editingControl, setEditingControl] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<ISOControl>>({});

  const isAdmin = user?.role === 'admin';

  const handleSort = (key: keyof ISOControl) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleEditControl = (control: ISOControl) => {
    setEditingControl(control.id);
    setEditForm({
      status: control.status,
      priority: control.priority,
      dueDate: control.dueDate
    });
  };

  const handleSaveEdit = () => {
    if (editingControl && editForm) {
      updateControl(editingControl, editForm);
      setEditingControl(null);
      setEditForm({});
    }
  };

  const handleCancelEdit = () => {
    setEditingControl(null);
    setEditForm({});
  };
  const sortedControls = React.useMemo(() => {
    if (!sortConfig) return filteredControls;

    return [...filteredControls].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredControls, sortConfig]);

  const paginatedControls = sortedControls.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const totalPages = Math.ceil(sortedControls.length / pageSize);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'in_progress': return <Clock className="w-4 h-4 text-blue-500" />;
      case 'review': return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      default: return <AlertCircle className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return 'Completado';
      case 'in_progress': return 'En Progreso';
      case 'review': return 'En Revisión';
      case 'not_started': return 'No Iniciado';
      default: return 'No Conforme';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      case 'high': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
      case 'medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      default: return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
    }
  };

  const getPriorityText = (priority: string) => {
    switch (priority) {
      case 'critical': return 'Crítica';
      case 'high': return 'Alta';
      case 'medium': return 'Media';
      default: return 'Baja';
    }
  };

  const handleControlClick = (control: ISOControl) => {
    const selectedWithBreadcrumb: SelectedControl = {
      ...control,
      breadcrumb: [control.category, control.subcategory, control.title]
    };
    onSelectControl(selectedWithBreadcrumb);
  };

  const handleAssignControl = (control: ISOControl) => {
    setAssignmentModal(control);
  };

  return (
    <>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Table Header */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Controles ISO 27001/27002
            </h3>
            <span className="px-2 py-1 text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full">
              {sortedControls.length} controles
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value={10}>10 por página</option>
              <option value={25}>25 por página</option>
              <option value={50}>50 por página</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              {[
                { key: 'code', label: 'Código' },
                { key: 'title', label: 'Control' },
                { key: 'status', label: 'Estado' },
                { key: 'tasks', label: 'Tareas' },
                { key: 'priority', label: 'Prioridad' },
                { key: 'dueDate', label: 'Fecha Límite' },
                { key: 'assignedReferent', label: 'Responsable' }
              ].map((column) => (
                <th
                  key={column.key}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                  onClick={() => handleSort(column.key as keyof ISOControl)}
                >
                  <div className="flex items-center space-x-1">
                    <span>{column.label}</span>
                    {sortConfig?.key === column.key && (
                      sortConfig.direction === 'asc' ? 
                        <ChevronUp className="w-3 h-3" /> : 
                        <ChevronDown className="w-3 h-3" />
                    )}
                  </div>
                </th>
              ))}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {paginatedControls.map((control) => (
              <tr 
                key={control.id}
                className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                onClick={(e) => {
                  // Don't trigger row click if clicking on interactive elements
                  if ((e.target as HTMLElement).tagName === 'SELECT' || 
                      (e.target as HTMLElement).tagName === 'INPUT' ||
                      (e.target as HTMLElement).tagName === 'BUTTON' ||
                      (e.target as HTMLElement).closest('button') ||
                      (e.target as HTMLElement).closest('select')) {
                    return;
                  }
                  handleControlClick(control);
                }}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                    {control.code}
                  </span>
                </td>
                <td className="px-6 py-4 group relative">
                  <div className="max-w-md">
                    <p
                      className="text-sm font-medium text-gray-900 dark:text-white line-clamp-2 group-hover:line-clamp-none transition-all"
                      title={control.title}
                    >
                      {control.title}
                    </p>
                    <p
                      className="text-xs text-gray-500 dark:text-gray-400 truncate group-hover:whitespace-normal transition-all"
                      title={control.category}
                    >
                      {control.category}
                    </p>
                  </div>
                  {/* Fondo expandido en hover para mejor legibilidad */}
                  <div className="hidden group-hover:block absolute inset-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 shadow-lg rounded-lg z-10 px-6 py-4">
                    <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                      {control.title}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {control.category}
                    </p>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {editingControl === control.id ? (
                    <select
                      value={editForm.status}
                      onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                      className="text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <option value="not_started">No Iniciado</option>
                      <option value="in_progress">En Progreso</option>
                      <option value="review">En Revisión</option>
                      <option value="completed">Completado</option>
                      <option value="non_compliant">No Conforme</option>
                    </select>
                  ) : (
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(control.status)}
                      <span className="text-sm text-gray-900 dark:text-white">
                        {getStatusText(control.status)}
                      </span>
                    </div>
                  )}
                </td>
                {/* Columna de Tareas */}
                <td className="px-6 py-4 whitespace-nowrap">
                  {(() => {
                    const tasks = control.tasks || [];
                    const totalTasks = tasks.length;
                    const completedTasks = tasks.filter(t => t.status === 'completed').length;
                    const inProgressTasks = tasks.filter(t => t.status === 'in_progress').length;
                    const pendingTasks = tasks.filter(t => t.status === 'pending').length;
                    const completionPercentage = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

                    if (totalTasks === 0) {
                      return (
                        <div className="flex items-center text-gray-400 dark:text-gray-500">
                          <span className="text-sm">Sin tareas</span>
                        </div>
                      );
                    }

                    const getProgressColor = () => {
                      if (completionPercentage === 100) return 'bg-green-500';
                      if (completionPercentage > 0) return 'bg-blue-500';
                      return 'bg-gray-300 dark:bg-gray-600';
                    };

                    const getTextColor = () => {
                      if (completionPercentage === 100) return 'text-green-600 dark:text-green-400';
                      if (completionPercentage > 0) return 'text-blue-600 dark:text-blue-400';
                      return 'text-gray-600 dark:text-gray-400';
                    };

                    return (
                      <div
                        className="space-y-1"
                        title={`${completedTasks} completadas, ${inProgressTasks} en progreso, ${pendingTasks} pendientes`}
                      >
                        <div className="flex items-center space-x-2">
                          <span className={`text-sm font-medium ${getTextColor()}`}>
                            {completedTasks}/{totalTasks}
                          </span>
                          {completionPercentage === 100 && (
                            <span className="text-green-500">✓</span>
                          )}
                        </div>
                        {/* Barra de progreso */}
                        <div className="w-20 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${getProgressColor()} transition-all duration-300`}
                            style={{ width: `${completionPercentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {editingControl === control.id ? (
                    <select
                      value={editForm.priority}
                      onChange={(e) => setEditForm({ ...editForm, priority: e.target.value })}
                      className="text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <option value="low">Baja</option>
                      <option value="medium">Media</option>
                      <option value="high">Alta</option>
                      <option value="critical">Crítica</option>
                    </select>
                  ) : (
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(control.priority)}`}>
                      {getPriorityText(control.priority)}
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {editingControl === control.id ? (
                    <input
                      type="date"
                      value={editForm.dueDate}
                      onChange={(e) => setEditForm({ ...editForm, dueDate: e.target.value })}
                      className="text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <div className="flex items-center text-sm text-gray-900 dark:text-white">
                      <Calendar className="w-4 h-4 mr-1 text-gray-400" />
                      {new Date(control.dueDate).toLocaleDateString()}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-1">
                    {(control.assignedReferents || []).length > 0 ? (
                      <div className="space-y-1 max-w-xs">
                        {(control.assignedReferents || []).map((referent) => (
                          <div key={referent.id} className="flex items-center space-x-2">
                            {referent.avatar ? (
                              <img 
                                src={referent.avatar} 
                                alt={referent.name}
                                className="w-6 h-6 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
                                <User className="w-3 h-3 text-white" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {referent.name}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center text-gray-400">
                        <Users className="w-4 h-4 mr-1" />
                        <span className="text-sm">Sin asignar</span>
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  {editingControl === control.id ? (
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          console.log(`💾 [SAVING CONTROL EDIT] Control: ${control.code}`);
                          handleSaveEdit();
                        }}
                        className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-400 hover:text-green-600 dark:hover:text-green-400"
                        title="Guardar cambios"
                      >
                        <Save className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          console.log(`❌ [CANCELING CONTROL EDIT] Control: ${control.code}`);
                          handleCancelEdit();
                        }}
                        className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                        title="Cancelar edición"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          console.log(`👁️ [VIEWING CONTROL] ${control.code}: ${control.title}`);
                          handleControlClick(control);
                        }}
                        className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        title="Ver detalles"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {isAdmin && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log(`👥 [ASSIGNING CONTROL] ${control.code} to referents`);
                            handleAssignControl(control);
                          }}
                          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                          title="Asignar control"
                        >
                          <UserPlus className="w-4 h-4" />
                        </button>
                      )}
                      {isAdmin && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log(`✏️ [EDITING CONTROL] ${control.code}`);
                            handleEditControl(control);
                          }}
                          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          title="Editar control"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700 dark:text-gray-300">
              Mostrando {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, sortedControls.length)} de {sortedControls.length} controles
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Anterior
              </button>
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Página {currentPage} de {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Siguiente
              </button>
            </div>
          </div>
        </div>
      )}
      </div>

      {/* Assignment Modal */}
      {assignmentModal && (
        <ControlAssignmentModal
          control={assignmentModal}
          onClose={() => setAssignmentModal(null)}
          onAssign={(referentIds) => {
            if (assignmentModal) {
              assignControlToReferent(assignmentModal.id, referentIds);
              setAssignmentModal(null);
            }
          }}
        />
      )}
    </>
  );
}